import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Headset, Loader2, ShieldOff } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import CustomerServiceDetailsForm from '@/components/admin/crm/CustomerServiceDetailsForm';
import MacroLibraryPanel from '@/components/admin/crm/MacroLibraryPanel';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// Support enquiries and returns are one destination in the sidebar, joined by
// this strip. Each keeps its own route, data and actions.
const SECTION_TABS = [
  { to: '/admin/customer-service', label: 'SUPPORT ENQUIRIES' },
  { to: '/admin/returns', label: 'RETURNS & RMAS' },
];

const TABS = [
  { id: 'details', label: 'SERVICE DETAILS' },
  { id: 'macros', label: 'CANNED RESPONSES' },
];

export default function AdminCustomerService() {
  const { user, isLoadingAuth } = useAuth();
  const [tab, setTab] = useState('details');

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Headset className="w-4 h-4 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">SYSTEM &amp; INTEGRATIONS</p>
          </div>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">CUSTOMER SERVICE</h1>
          <p className="text-white/30 text-sm mt-1">
            Your support contact details power the public <Link to="/customer-service" className="text-primary hover:underline">/customer-service</Link> page and your Google Merchant Center customer service attributes.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <AdminSectionTabs tabs={SECTION_TABS} className="mt-6" />
      </div>

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-4 text-[10px] font-bold tracking-widest whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-white/40 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
        {tab === 'details' ? (
          <div className="max-w-3xl">
            <CustomerServiceDetailsForm />
          </div>
        ) : (
          <MacroLibraryPanel />
        )}
      </section>
    </div>
  );
}